import { useState, useMemo } from 'react';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    ArrowRight,
    FilterLines,
    XClose
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import {
    useValidationRules,
    useCreateValidationRule,
    useUpdateValidationRule,
    useDeleteValidationRule
} from '@/hooks/api/use-validation-rules';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useDocumentAttributes } from '@/hooks/api/use-document-attributes';
import { ComparisonType, ValidationSeverity } from '@/types/api/validation-rules';
import type { ValidationRuleResponse, ValidationRuleCreate, ValidationRuleUpdate, ToleranceConfig } from '@/types/api/validation-rules';

export const DocConnectionManagerPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<ValidationRuleResponse | null>(null);

    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [filterBlocking, setFilterBlocking] = useState<'all' | 'blocking' | 'non-blocking'>('all');
    const [filterComparisonTypes, setFilterComparisonTypes] = useState<Set<ComparisonType>>(new Set());
    const [filterSeverities, setFilterSeverities] = useState<Set<ValidationSeverity>>(new Set());

    // Fetch validation rules from API
    const { data: rules = [], isLoading: isLoadingRules, isError } = useValidationRules();

    // Fetch document types and attributes for dropdowns
    const { data: documentTypes = [], isLoading: isLoadingTypes } = useDocumentTypes({ limit: 500 });
    const { data: attributes = [], isLoading: isLoadingAttributes } = useDocumentAttributes({ limit: 500 });

    // Mutations
    const createRule = useCreateValidationRule();
    const updateRule = useUpdateValidationRule();
    const deleteRule = useDeleteValidationRule();

    const isLoading = isLoadingRules || isLoadingTypes || isLoadingAttributes;

    // Form state
    const [formData, setFormData] = useState<{
        rule_name: string;
        rule_code: string;
        description: string;
        source_document_type: string;
        target_document_type: string;
        source_attribute_id: string;
        target_attribute_id: string;
        comparison_type: ComparisonType;
        is_blocking: boolean;
        severity_if_fail: ValidationSeverity;
        tolerance_type: string;
        tolerance_value: string;
        is_active: boolean;
    }>({
        rule_name: '',
        rule_code: '',
        description: '',
        source_document_type: '',
        target_document_type: '',
        source_attribute_id: '',
        target_attribute_id: '',
        comparison_type: ComparisonType.IDENTITY,
        is_blocking: false,
        severity_if_fail: ValidationSeverity.WARNING,
        tolerance_type: '',
        tolerance_value: '',
        is_active: true,
    });

    // Document type options
    const documentTypeOptions = useMemo(() => {
        return documentTypes.map((dt) => ({ id: dt.key, label: dt.name }));
    }, [documentTypes]);

    // Attribute options
    const attributeOptions = useMemo(() => {
        return attributes.map((attr) => ({
            id: attr.document_attribute_id,
            label: `${attr.label} (${attr.attribute_key})`
        }));
    }, [attributes]);

    // Comparison type options
    const comparisonTypeOptions = [
        { id: ComparisonType.IDENTITY, label: t('connections.comparisonTypes.identity', { defaultValue: 'Identity (exact match)' }) },
        { id: ComparisonType.COHERENCE, label: t('connections.comparisonTypes.coherence', { defaultValue: 'Coherence (semantic match)' }) },
        { id: ComparisonType.TEMPORAL, label: t('connections.comparisonTypes.temporal', { defaultValue: 'Temporal (date logic)' }) },
        { id: ComparisonType.HIERARCHICAL, label: t('connections.comparisonTypes.hierarchical', { defaultValue: 'Hierarchical (precedence)' }) },
    ];

    // Severity options
    const severityOptions = [
        { id: ValidationSeverity.INFO, label: t('connections.severity.info', { defaultValue: 'Info' }) },
        { id: ValidationSeverity.WARNING, label: t('connections.severity.warning', { defaultValue: 'Warning' }) },
        { id: ValidationSeverity.ERROR, label: t('connections.severity.error', { defaultValue: 'Error' }) },
        { id: ValidationSeverity.CRITICAL, label: t('connections.severity.critical', { defaultValue: 'Critical' }) },
    ];

    // Tolerance type options
    const toleranceTypeOptions = [
        { id: '', label: t('connections.tolerance.none', { defaultValue: 'No tolerance' }) },
        { id: 'percentage', label: t('connections.tolerance.percentage', { defaultValue: 'Percentage (%)' }) },
        { id: 'absolute', label: t('connections.tolerance.absolute', { defaultValue: 'Absolute value' }) },
        { id: 'days', label: t('connections.tolerance.days', { defaultValue: 'Days' }) },
    ];

    // Toggle comparison type filter
    const toggleComparisonTypeFilter = (type: ComparisonType) => {
        setFilterComparisonTypes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(type)) {
                newSet.delete(type);
            } else {
                newSet.add(type);
            }
            return newSet;
        });
    };

    // Toggle severity filter
    const toggleSeverityFilter = (severity: ValidationSeverity) => {
        setFilterSeverities(prev => {
            const newSet = new Set(prev);
            if (newSet.has(severity)) {
                newSet.delete(severity);
            } else {
                newSet.add(severity);
            }
            return newSet;
        });
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilterBlocking('all');
        setFilterComparisonTypes(new Set());
        setFilterSeverities(new Set());
        setSearchTerm('');
    };

    // Check if any filters are active
    const hasActiveFilters = filterBlocking !== 'all' || filterComparisonTypes.size > 0 || filterSeverities.size > 0;

    // Filter rules by search term and filters
    const filteredRules = useMemo(() => {
        let result = rules;

        // Apply search term filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter((rule) =>
                rule.rule_name.toLowerCase().includes(term) ||
                rule.rule_code.toLowerCase().includes(term) ||
                rule.source_document_type.toLowerCase().includes(term) ||
                rule.target_document_type.toLowerCase().includes(term)
            );
        }

        // Apply blocking filter
        if (filterBlocking === 'blocking') {
            result = result.filter((rule) => rule.is_blocking);
        } else if (filterBlocking === 'non-blocking') {
            result = result.filter((rule) => !rule.is_blocking);
        }

        // Apply comparison type filter
        if (filterComparisonTypes.size > 0) {
            result = result.filter((rule) => filterComparisonTypes.has(rule.comparison_type as ComparisonType));
        }

        // Apply severity filter
        if (filterSeverities.size > 0) {
            result = result.filter((rule) => filterSeverities.has(rule.severity_if_fail as ValidationSeverity));
        }

        return result;
    }, [rules, searchTerm, filterBlocking, filterComparisonTypes, filterSeverities]);

    const handleOpenModal = (rule?: ValidationRuleResponse) => {
        if (rule) {
            setEditingRule(rule);
            const tolerance = rule.tolerance_config;
            setFormData({
                rule_name: rule.rule_name,
                rule_code: rule.rule_code,
                description: rule.description || '',
                source_document_type: rule.source_document_type,
                target_document_type: rule.target_document_type,
                source_attribute_id: rule.source_attribute_id,
                target_attribute_id: rule.target_attribute_id,
                comparison_type: rule.comparison_type as ComparisonType,
                is_blocking: rule.is_blocking,
                severity_if_fail: rule.severity_if_fail as ValidationSeverity,
                tolerance_type: tolerance?.type || '',
                tolerance_value: tolerance?.value?.toString() || '',
                is_active: rule.is_active,
            });
        } else {
            setEditingRule(null);
            setFormData({
                rule_name: '',
                rule_code: '',
                description: '',
                source_document_type: '',
                target_document_type: '',
                source_attribute_id: '',
                target_attribute_id: '',
                comparison_type: ComparisonType.IDENTITY,
                is_blocking: false,
                severity_if_fail: ValidationSeverity.WARNING,
                tolerance_type: '',
                tolerance_value: '',
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingRule(null);
    };

    const handleSave = async () => {
        // Build tolerance config if specified
        let toleranceConfig: ToleranceConfig | undefined = undefined;
        if (formData.tolerance_type && formData.tolerance_value) {
            toleranceConfig = {
                type: formData.tolerance_type as 'percentage' | 'absolute' | 'days',
                value: parseFloat(formData.tolerance_value),
            };
        }

        try {
            if (editingRule) {
                // Update existing rule
                const updateData: ValidationRuleUpdate = {
                    rule_name: formData.rule_name,
                    description: formData.description || null,
                    source_document_type: formData.source_document_type || null,
                    target_document_type: formData.target_document_type || null,
                    source_attribute_id: formData.source_attribute_id || null,
                    target_attribute_id: formData.target_attribute_id || null,
                    comparison_type: formData.comparison_type || null,
                    is_blocking: formData.is_blocking,
                    severity_if_fail: formData.severity_if_fail,
                    tolerance_config: toleranceConfig || null,
                    is_active: formData.is_active,
                };
                await updateRule.mutateAsync({
                    ruleId: editingRule.cross_check_rule_id,
                    data: updateData,
                });
                toast.success(t('connections.toast.updateSuccess', { defaultValue: 'Rule updated successfully' }));
            } else {
                // Create new rule
                const createData: ValidationRuleCreate = {
                    rule_name: formData.rule_name,
                    rule_code: formData.rule_code,
                    description: formData.description || undefined,
                    source_document_type: formData.source_document_type,
                    target_document_type: formData.target_document_type,
                    source_attribute_id: formData.source_attribute_id,
                    target_attribute_id: formData.target_attribute_id,
                    comparison_type: formData.comparison_type,
                    is_blocking: formData.is_blocking,
                    severity_if_fail: formData.severity_if_fail,
                    tolerance_config: toleranceConfig,
                    is_active: formData.is_active,
                };
                await createRule.mutateAsync(createData);
                toast.success(t('connections.toast.createSuccess', { defaultValue: 'Rule created successfully' }));
            }
            handleCloseModal();
        } catch (error) {
            console.error('Error saving rule:', error);
            toast.error(t('connections.toast.error', { defaultValue: 'Error saving rule' }));
        }
    };

    const handleDelete = async (ruleId: string) => {
        if (confirm(t('modal.confirmDelete'))) {
            try {
                await deleteRule.mutateAsync(ruleId);
                toast.success(t('connections.toast.deleteSuccess', { defaultValue: 'Rule deleted successfully' }));
            } catch (error) {
                console.error('Error deleting rule:', error);
                toast.error(t('connections.toast.error', { defaultValue: 'Error deleting rule' }));
            }
        }
    };

    // Helper to get attribute label by ID
    const getAttributeLabel = (attributeId: string) => {
        const attr = attributes.find((a) => a.document_attribute_id === attributeId);
        return attr?.label || attributeId;
    };

    // Helper to get document type label
    const getDocTypeLabel = (docType: string) => {
        const dt = documentTypes.find((d) => d.key === docType);
        return dt?.name || docType;
    };

    // Helper to get severity badge color
    const getSeverityColor = (severity: string): 'gray' | 'blue' | 'warning' | 'error' => {
        switch (severity) {
            case 'info': return 'gray';
            case 'warning': return 'warning';
            case 'error': return 'error';
            case 'critical': return 'error';
            default: return 'gray';
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error mb-4">{t('connections.error.loading', { defaultValue: 'Error loading validation rules' })}</p>
                    <Button onClick={() => window.location.reload()} size="md" color="primary">
                        {t('common.retry', { defaultValue: 'Retry' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('connections.title')}</h2>
                    <p className="text-tertiary mt-1">{t('connections.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('connections.newConnection')}
                </Button>
            </div>

            {/* Search and Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary space-y-4">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <Input
                            placeholder={t('connections.searchPlaceholder')}
                            value={searchTerm}
                            onChange={setSearchTerm}
                            icon={SearchLg}
                            size="md"
                        />
                    </div>
                    <Button
                        onClick={() => setShowFilters(!showFilters)}
                        size="md"
                        color={showFilters || hasActiveFilters ? 'primary' : 'secondary'}
                        iconLeading={FilterLines}
                    >
                        {t('connections.filters.toggle', { defaultValue: 'Filters' })}
                        {hasActiveFilters && (
                            <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-white/20 rounded-full">
                                {(filterBlocking !== 'all' ? 1 : 0) + filterComparisonTypes.size + filterSeverities.size}
                            </span>
                        )}
                    </Button>
                    {hasActiveFilters && (
                        <Button
                            onClick={clearAllFilters}
                            size="md"
                            color="secondary"
                            iconLeading={XClose}
                        >
                            {t('connections.filters.clear', { defaultValue: 'Clear' })}
                        </Button>
                    )}
                </div>

                {/* Filter Panel */}
                {showFilters && (
                    <div className="pt-4 border-t border-secondary space-y-4">
                        {/* Blocking Filter */}
                        <div>
                            <h4 className="text-sm font-medium text-primary mb-2">
                                {t('connections.filters.blocking', { defaultValue: 'Blocking Status' })}
                            </h4>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="blocking"
                                        checked={filterBlocking === 'all'}
                                        onChange={() => setFilterBlocking('all')}
                                        className="w-4 h-4 text-brand border-secondary focus:ring-brand"
                                    />
                                    <span className="text-sm text-secondary">{t('connections.filters.all', { defaultValue: 'All' })}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="blocking"
                                        checked={filterBlocking === 'blocking'}
                                        onChange={() => setFilterBlocking('blocking')}
                                        className="w-4 h-4 text-brand border-secondary focus:ring-brand"
                                    />
                                    <span className="text-sm text-secondary">{t('connections.filters.blockingOnly', { defaultValue: 'Blocking only' })}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="blocking"
                                        checked={filterBlocking === 'non-blocking'}
                                        onChange={() => setFilterBlocking('non-blocking')}
                                        className="w-4 h-4 text-brand border-secondary focus:ring-brand"
                                    />
                                    <span className="text-sm text-secondary">{t('connections.filters.nonBlocking', { defaultValue: 'Non-blocking only' })}</span>
                                </label>
                            </div>
                        </div>

                        {/* Comparison Type Filter */}
                        <div>
                            <h4 className="text-sm font-medium text-primary mb-2">
                                {t('connections.filters.comparisonType', { defaultValue: 'Comparison Type' })}
                            </h4>
                            <div className="flex flex-wrap items-center gap-3">
                                {comparisonTypeOptions.map((option) => (
                                    <Checkbox
                                        key={option.id}
                                        isSelected={filterComparisonTypes.has(option.id)}
                                        onChange={() => toggleComparisonTypeFilter(option.id)}
                                        size="sm"
                                        label={option.label}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Severity Filter */}
                        <div>
                            <h4 className="text-sm font-medium text-primary mb-2">
                                {t('connections.filters.severity', { defaultValue: 'Severity' })}
                            </h4>
                            <div className="flex flex-wrap items-center gap-3">
                                {severityOptions.map((option) => (
                                    <Checkbox
                                        key={option.id}
                                        isSelected={filterSeverities.has(option.id)}
                                        onChange={() => toggleSeverityFilter(option.id)}
                                        size="sm"
                                        label={
                                            <Badge color={getSeverityColor(option.id)} size="sm">
                                                {option.label}
                                            </Badge>
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Rules Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('connections.title')} (${filteredRules.length})`}
                    description={t('connections.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('connections.headers.ruleName', { defaultValue: 'Rule Name' })} isRowHeader />
                        <Table.Head label={t('connections.headers.source')} />
                        <Table.Head label={t('connections.headers.target')} />
                        <Table.Head label={t('connections.headers.comparisonType', { defaultValue: 'Comparison' })} />
                        <Table.Head label={t('connections.headers.severity', { defaultValue: 'Severity' })} />
                        <Table.Head label={t('connections.headers.blocking', { defaultValue: 'Blocking' })} />
                        <Table.Head label={t('connections.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredRules.map((rule) => (
                            <Table.Row key={rule.cross_check_rule_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{rule.rule_name}</div>
                                    <div className="text-sm text-tertiary">{rule.rule_code}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="text-sm text-primary">
                                        {getDocTypeLabel(rule.source_document_type)}
                                    </div>
                                    <div className="text-xs text-tertiary">
                                        {getAttributeLabel(rule.source_attribute_id)}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <ArrowRight className="size-4 text-tertiary" />
                                        <div>
                                            <div className="text-sm text-primary">
                                                {getDocTypeLabel(rule.target_document_type)}
                                            </div>
                                            <div className="text-xs text-tertiary">
                                                {getAttributeLabel(rule.target_attribute_id)}
                                            </div>
                                        </div>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="blue" size="sm">
                                        {comparisonTypeOptions.find((c) => c.id === rule.comparison_type)?.label || rule.comparison_type}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getSeverityColor(rule.severity_if_fail)} size="sm">
                                        {severityOptions.find((s) => s.id === rule.severity_if_fail)?.label || rule.severity_if_fail}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    {rule.is_blocking ? (
                                        <Badge color="error" size="sm">{t('common.yes', { defaultValue: 'Yes' })}</Badge>
                                    ) : (
                                        <Badge color="gray" size="sm">{t('common.no', { defaultValue: 'No' })}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(rule)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('connections.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(rule.cross_check_rule_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('connections.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredRules.length === 0 && !isLoading && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-h-[90vh] overflow-y-auto">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingRule ? t('connections.modal.edit') : t('connections.modal.new')}
                                </h2>

                                <div className="space-y-6">
                                    {/* Basic Info */}
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('connections.modal.fields.ruleName', { defaultValue: 'Rule Name' })}
                                            placeholder={t('connections.modal.fields.ruleNamePlaceholder', { defaultValue: 'e.g., Invoice Number Match' })}
                                            value={formData.rule_name}
                                            onChange={(value) => setFormData({ ...formData, rule_name: value })}
                                            isRequired
                                            size="md"
                                        />

                                        <Input
                                            label={t('connections.modal.fields.ruleCode', { defaultValue: 'Rule Code' })}
                                            placeholder={t('connections.modal.fields.ruleCodePlaceholder', { defaultValue: 'e.g., invoice_number_match' })}
                                            value={formData.rule_code}
                                            onChange={(value) => setFormData({ ...formData, rule_code: value })}
                                            isRequired
                                            size="md"
                                            isDisabled={!!editingRule}
                                        />
                                    </div>

                                    <Input
                                        label={t('connections.modal.fields.description', { defaultValue: 'Description' })}
                                        placeholder={t('connections.modal.fields.descriptionPlaceholder', { defaultValue: 'Describe what this rule validates' })}
                                        value={formData.description}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    {/* Source Section */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">{t('connections.modal.source')}</h3>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select.ComboBox
                                                label={t('connections.modal.fields.sourceDocumentType')}
                                                items={documentTypeOptions}
                                                selectedKey={formData.source_document_type}
                                                onSelectionChange={(key) => setFormData({ ...formData, source_document_type: key as string })}
                                                placeholder={t('connections.modal.fields.searchDocumentType', { defaultValue: 'Cerca tipo documento...' })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select.ComboBox>

                                            <Select.ComboBox
                                                label={t('connections.modal.fields.sourceAttribute')}
                                                items={attributeOptions}
                                                selectedKey={formData.source_attribute_id}
                                                onSelectionChange={(key) => setFormData({ ...formData, source_attribute_id: key as string })}
                                                placeholder={t('connections.modal.fields.searchAttribute', { defaultValue: 'Cerca attributo...' })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select.ComboBox>
                                        </div>
                                    </div>

                                    {/* Target Section */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">{t('connections.modal.target')}</h3>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select.ComboBox
                                                label={t('connections.modal.fields.targetDocumentType')}
                                                items={documentTypeOptions}
                                                selectedKey={formData.target_document_type}
                                                onSelectionChange={(key) => setFormData({ ...formData, target_document_type: key as string })}
                                                placeholder={t('connections.modal.fields.searchDocumentType', { defaultValue: 'Cerca tipo documento...' })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select.ComboBox>

                                            <Select.ComboBox
                                                label={t('connections.modal.fields.targetAttribute')}
                                                items={attributeOptions}
                                                selectedKey={formData.target_attribute_id}
                                                onSelectionChange={(key) => setFormData({ ...formData, target_attribute_id: key as string })}
                                                placeholder={t('connections.modal.fields.searchAttribute', { defaultValue: 'Cerca attributo...' })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select.ComboBox>
                                        </div>
                                    </div>

                                    {/* Validation Configuration */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">
                                            {t('connections.modal.validationConfig', { defaultValue: 'Validation Configuration' })}
                                        </h3>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select
                                                label={t('connections.modal.fields.comparisonType', { defaultValue: 'Comparison Type' })}
                                                items={comparisonTypeOptions}
                                                selectedKey={formData.comparison_type}
                                                onSelectionChange={(key) => setFormData({ ...formData, comparison_type: key as ComparisonType })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>

                                            <Select
                                                label={t('connections.modal.fields.severity', { defaultValue: 'Severity if Fail' })}
                                                items={severityOptions}
                                                selectedKey={formData.severity_if_fail}
                                                onSelectionChange={(key) => setFormData({ ...formData, severity_if_fail: key as ValidationSeverity })}
                                                isRequired
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                isSelected={formData.is_blocking}
                                                onChange={(checked: boolean) => setFormData({ ...formData, is_blocking: checked })}
                                            >
                                                <span className="text-sm font-medium text-primary">
                                                    {t('connections.modal.fields.isBlocking', { defaultValue: 'Blocking Rule' })}
                                                </span>
                                            </Checkbox>
                                            <span className="text-xs text-tertiary">
                                                {t('connections.modal.fields.isBlockingHelp', { defaultValue: 'If checked, shipment will be blocked when this validation fails' })}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Tolerance Configuration */}
                                    <div className="space-y-4 p-4 rounded-lg bg-secondary">
                                        <h3 className="text-lg font-semibold text-primary">
                                            {t('connections.modal.toleranceConfig', { defaultValue: 'Tolerance Configuration' })}
                                        </h3>
                                        <p className="text-sm text-tertiary">
                                            {t('connections.modal.toleranceHelp', { defaultValue: 'Optional: Allow minor differences in numeric or date values' })}
                                        </p>

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <Select
                                                label={t('connections.modal.fields.toleranceType', { defaultValue: 'Tolerance Type' })}
                                                items={toleranceTypeOptions}
                                                selectedKey={formData.tolerance_type}
                                                onSelectionChange={(key) => setFormData({ ...formData, tolerance_type: key as string })}
                                                size="md"
                                            >
                                                {(item) => (
                                                    <Select.Item key={item.id} id={item.id}>
                                                        {item.label}
                                                    </Select.Item>
                                                )}
                                            </Select>

                                            {formData.tolerance_type && (
                                                <Input
                                                    label={t('connections.modal.fields.toleranceValue', { defaultValue: 'Tolerance Value' })}
                                                    placeholder={formData.tolerance_type === 'percentage' ? 'e.g., 5' : formData.tolerance_type === 'days' ? 'e.g., 14' : 'e.g., 100'}
                                                    value={formData.tolerance_value}
                                                    onChange={(value) => setFormData({ ...formData, tolerance_value: value })}
                                                    size="md"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {(createRule.isPending || updateRule.isPending) && (
                                    <div className="mt-4 text-sm text-tertiary">
                                        {t('connections.modal.saving', { defaultValue: 'Saving...' })}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={handleCloseModal}
                                        size="md"
                                        color="secondary"
                                        isDisabled={createRule.isPending || updateRule.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        size="md"
                                        color="primary"
                                        isDisabled={createRule.isPending || updateRule.isPending}
                                    >
                                        {editingRule ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
